import { PWADatabase } from './db';

export const db = new PWADatabase();

type ImportItem = { id?: number | string; status?: string; post_status?: string };

function asArray<T = any>(value: unknown): T[] {
	return Array.isArray(value) ? (value as T[]) : [];
}

function toFiniteNumber(value: unknown): number | null {
	const num = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(num) ? num : null;
}

function isPublishStatus(item: ImportItem): boolean {
	const status = item.status ?? item.post_status;
	return !status || status === 'publish';
}

function normalizeCollection(value: unknown): any[] {
	return asArray<ImportItem>(value).filter((item) => {
		const id = toFiniteNumber(item?.id);
		return id !== null && isPublishStatus(item);
	});
}

function toIdSet(items: ImportItem[]): Set<number> {
	return new Set(
		items
			.map((item) => toFiniteNumber(item.id))
			.filter((id): id is number => id !== null),
	);
}

function filterRelationIds(value: unknown, validIds: Set<number>): number[] {
	return asArray<number | string>(value)
		.map((id) => toFiniteNumber(id))
		.filter((id): id is number => id !== null && validIds.has(id));
}

function sanitizeCombinedData(data: any) {
	const trails = normalizeCollection(data?.data_trail);
	const places = normalizeCollection(data?.data_place);
	const stories = normalizeCollection(data?.data_story);
	const activities = normalizeCollection(data?.data_activity);
	const topics = normalizeCollection(data?.data_topic);

	const trailIds = toIdSet(trails);
	const placeIds = toIdSet(places);

	const sanitizedPlaces = places.map((place: any) => ({
		...place,
		fkTrails: filterRelationIds(place?.fkTrails, trailIds),
	}));

	const sanitizedStories = stories.map((story: any) => ({
		...story,
		fkTrails: filterRelationIds(story?.fkTrails, trailIds),
		fkPlaces: filterRelationIds(story?.fkPlaces, placeIds),
	}));

	return {
		data_place: sanitizedPlaces,
		data_trail: trails,
		data_story: sanitizedStories,
		data_activity: activities,
		data_topic: topics,
	};
}

export async function resetDatabase() {
	return db.transaction(
		'rw',
		db.places,
		db.trails,
		db.stories,
		db.activities,
		db.topics,
		async () => {
			await Promise.all([
				db.places.clear(),
				db.trails.clear(),
				db.stories.clear(),
				db.activities.clear(),
				db.topics.clear(),
			]);
		},
	);
}

export async function remoteSync() {
	const SERVER_URL = 'https://mapadmin.northpennines.org.uk/';
	const statusResponse = await fetch(
		SERVER_URL + 'wp-json/c12/v1/status?cb=' + Date.now(),
	);
	if (!statusResponse.ok) {
		throw new Error(`Status request failed: ${statusResponse.status}`);
	}
	await statusResponse.json();

	const combinedResponse = await fetch(
		SERVER_URL + 'wp-json/c12/v1/combined?stores=trail,poi&cb=' + Date.now(),
	);
	if (!combinedResponse.ok) {
		throw new Error(`Combined request failed: ${combinedResponse.status}`);
	}

	const data = await combinedResponse.json();
	const cleanData = sanitizeCombinedData(data);
	await db.transaction(
		'rw',
		db.places,
		db.trails,
		db.stories,
		db.activities,
		db.topics,
		async () => {
			// Treat remote combined payload as the source of truth so deleted
			// records from older installs cannot linger in local IndexedDB.
			await Promise.all([
				db.places.clear(),
				db.trails.clear(),
				db.stories.clear(),
				db.activities.clear(),
				db.topics.clear(),
			]);

			await Promise.all([
				db.places.bulkPut(cleanData.data_place),
				db.trails.bulkPut(cleanData.data_trail),
				db.stories.bulkPut(cleanData.data_story),
				db.activities.bulkPut(cleanData.data_activity),
				db.topics.bulkPut(cleanData.data_topic),
			]);
		},
	);
}

async function isLocalContentDbHealthy() {
	try {
		await db.open();
		const [placeCount, trailCount, storyCount, activityCount, topicCount] =
			await Promise.all([
			db.places.count(),
			db.trails.count(),
			db.stories.count(),
			db.activities.count(),
			db.topics.count(),
		]);
		return (
			placeCount > 0 &&
			trailCount > 0 &&
			storyCount > 0 &&
			activityCount > 0 &&
			topicCount > 0
		);
	} catch (error) {
		console.warn('sync_db_health_check_failed', error);
		return false;
	}
}

export async function syncWithRecovery() {
	try {
		await remoteSync();
		console.info('sync_ok');
		return;
	} catch (error) {
		const dbHealthy = await isLocalContentDbHealthy();

		if (dbHealthy) {
			console.warn('sync_failed_db_healthy_using_cache', error);
			return;
		}

		console.warn('sync_failed_db_corrupt_reset_and_retry', error);

		try {
			await resetDatabase();
			await remoteSync();
			console.info('sync_recovery_ok');
		} catch (retryError) {
			console.error('sync_retry_failed', retryError);
		}
	}
}
